<template>
    <button
        type="button"
        :class="[
            full ? 'w-full' : '',
            hideText ? 'w-10 h-10' : 'h-10 px-4'
        ]"
        :disabled="loading"
        class="gsi-material-button inline-flex items-center justify-center gap-2 bg-white dark:bg-surface-card rounded-full transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
        @click="loginWithGoogle"
    >
        <div class="gsi-material-button-content-wrapper flex items-center justify-center gap-2">
            <!-- Google G Logo (official branding) -->
            <div class="gsi-material-button-icon flex-shrink-0" style="width: 20px; height: 20px;">
                <svg v-if="!loading" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" style="display: block; width: 20px; height: 20px;">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                </svg>
                <svg v-else class="animate-spin" style="width: 20px; height: 20px;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
            <span v-if="!hideText" class="gsi-material-button-contents whitespace-nowrap">
                {{ createAccount ? $t('auth.signUpWithGoogle') : $t('auth.signInWithGoogle') }}
            </span>
        </div>
    </button>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect } from 'firebase/auth'

import { CButtonSizes, type ButtonSize } from '@/types/buttons'


interface Props {
    createAccount?: boolean
    hideText?: boolean
    full?: boolean
    size?: ButtonSize
}

const props = withDefaults(defineProps<Props>(), {
    createAccount: false,
    hideText: false,
    full: false,
    size: CButtonSizes.Normal
})

const emit = defineEmits(['success', 'error'])

const { $firebase, $auth } = useNuxtApp()
const loading = ref(false)

async function loginWithGoogle() {
    try {
        loading.value = true
        const provider = new GoogleAuthProvider()

        // Add recommended scopes
        provider.addScope('profile')
        provider.addScope('email')

        // Set custom parameters to help with CORS issues
        provider.setCustomParameters({
            prompt: 'select_account'
        })

        const result = await signInWithPopup($auth, provider)

        // This gives you a Google Access Token. You can use it to access Google APIs.
        // const credential = GoogleAuthProvider.credentialFromResult(result)
        // const token = credential?.accessToken

        // The signed-in user info
        const user = result.user
        emit('success', user)
    } catch (error: any) {
        console.error('Google login error:', error)

        // Check for specific CORS/popup errors
        if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
            console.warn('Popup was blocked or closed by user')
        } else if (error.code === 'auth/cancelled-popup-request') {
            console.warn('Popup request was cancelled')
        }

        emit('error', error)
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
.gsi-material-button {
    -webkit-appearance: none;
    background-color: white;
    background-image: none;
    border: 1px solid #747775;
    box-sizing: border-box;
    color: #1f1f1f;
    cursor: pointer;
    /* Roboto via the theme variable: naming 'Roboto' here made @nuxt/fonts inline its @font-face into every page */
    font-family: var(--font-family-sans);
    font-size: 14px;
    letter-spacing: 0.25px;
    outline: none;
    overflow: hidden;
    position: relative;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
}

.gsi-material-button:not(:disabled):hover {
    box-shadow: 0 1px 2px 0 rgba(60, 64, 67, .30), 0 1px 3px 1px rgba(60, 64, 67, .15);
}

.gsi-material-button .gsi-material-button-contents {
    font-family: var(--font-family-sans);
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: top;
}

/* Neutral surfaces follow the selected theme; brand colors and images retain their colors. */
[data-theme="dark"] .gsi-material-button {
    background-color: var(--color-surface-card);
    color: var(--color-txt-headings);
}
</style>
